import {ReactNode, useEffect, useState} from "react";
import {useBeforeUnload, useBlocker} from "react-router";

import {CheckUnsavedModal} from "@/pages/checks/update/sections/CheckUnsavedModal.tsx";

interface UseNavigationBlockerOptions {
  shouldBlock: boolean;
  /**
   * Let navigations that stay on the same path through, so a page that keeps its own state in
   * the search params can go on updating them; only leaving the page is blocked.
   */
  allowSamePathNavigation?: boolean;
  onConfirm?: () => void;
  onUnblocked?: () => void;
}

interface UseNavigationBlockerReturn {
  showBlockerModal: () => void;
  NavigationBlockerModal: () => ReactNode;
}

export const useNavigationBlocker = ({
  shouldBlock,
  allowSamePathNavigation = false,
  onConfirm,
  onUnblocked,
}: UseNavigationBlockerOptions): UseNavigationBlockerReturn => {
  const [showModal, setShowModal] = useState(false);
  const blocker = useBlocker(
    ({currentLocation, nextLocation}) =>
      shouldBlock && !showModal && !(allowSamePathNavigation && currentLocation.pathname === nextLocation.pathname)
  );

  useBeforeUnload((event) => {
    if (shouldBlock) {
      event.preventDefault();
      return "You have unsaved changes. Are you sure you want to discard your changes?";
    }
  });

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    // shouldBlock may be stale at the moment navigation was blocked (e.g. a submit
    // handler that calls navigate() before its "success" state has committed).
    // Re-check the latest value here and auto-proceed if the guard has since cleared,
    // instead of always surfacing the modal.
    if (shouldBlock) {
      setShowModal(true);
    } else {
      blocker.proceed();
      setShowModal(false);
    }
  }, [blocker, shouldBlock]);

  const showBlockerModal = () => {
    if (shouldBlock) {
      setShowModal(true);
    } else {
      onUnblocked?.();
    }
  };

  const NavigationBlockerModal = () => (
    <CheckUnsavedModal
      show={showModal}
      onCancel={() => {
        if (blocker.state === "blocked") {
          blocker.reset();
        }
        setShowModal(false);
      }}
      onConfirm={() => {
        if (blocker.state === "blocked") {
          blocker.proceed();
        } else {
          onConfirm?.();
        }
        setShowModal(false);
      }}
    />
  );

  return {showBlockerModal, NavigationBlockerModal};
};
